import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  BarChart2,
  Bell,
  Link2,
  Users,
  BookOpen,
  Settings,
  CreditCard,
  LogOut,
  Shield,
  User
} from 'lucide-react';
import { signOut } from '../lib/supabase';
import { supabase } from '../lib/supabase';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin?: boolean;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isAdmin }) => {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = React.useState(0);

  React.useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { count } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('read', false);
        setUnreadCount(count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    window.addEventListener('notifications-read', fetchUnread);
    return () => {
      clearInterval(interval);
      window.removeEventListener('notifications-read', fetchUnread);
    };
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    {
      title: 'Principal',
      items: [
        { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
        { name: 'Alertas', icon: Bell, path: '/dashboard/notifications', badge: unreadCount },
        { name: 'Análisis', icon: BarChart2, path: '/dashboard/analyses' },
        { name: 'Diario de Trading', icon: BookOpen, path: '/dashboard/journal' },
      ],
    },
    {
      title: 'Marketing',
      items: [
        { name: 'Programa de Embajadores', icon: Users, path: '/dashboard/affiliates' },
        { name: 'Suscripción', icon: CreditCard, path: '/dashboard/subscription' },
      ],
    },
    {
      title: 'Cuenta',
      items: [
        { name: 'Mi Perfil', icon: User, path: '/dashboard/profile' },
        { name: 'Cuenta de Broker', icon: Link2, path: '/dashboard/broker-connection' },
        { name: 'Mi Cuenta Trading', icon: BarChart2, path: '/dashboard/trading-account' },
        { name: 'Ajustes', icon: Settings, path: '/dashboard/settings' },
        ...(isAdmin ? [{ name: 'Panel Admin', icon: Shield, path: '/dashboard/admin' }] : []),
      ],
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-dark-card border-r border-white/5 transform transition-transform duration-200 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-white/5">
          <Link to="/dashboard" className="flex items-center space-x-2">
            <img src="/logo-ati-icon.svg" alt="ATI" className="h-8 w-8" />
            <span className="text-lg font-bold text-white">Asistente Trading IA</span>
          </Link>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-8">
          {menuItems.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {section.title}
              </h3>
              <ul className="space-y-1">
                {section.items.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.path}
                      className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors duration-200 ${
                        isActive(item.path)
                          ? 'bg-primary text-white'
                          : 'text-gray-400 hover:text-white hover:bg-white/5'
                      }`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="flex-1">{item.name}</span>
                      {'badge' in item && (item as any).badge > 0 && (
                        <span className="bg-primary text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                          {(item as any).badge}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {/* Sign Out Button */}
        <div className="absolute bottom-4 left-4 right-4">
          <button
            onClick={handleSignOut}
            className="flex items-center space-x-2 w-full px-3 py-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors duration-200"
          >
            <LogOut className="h-5 w-5" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;